import { Injectable, NgZone } from '@angular/core'
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http'
import { Observable } from 'rxjs'

/**
 * Wraps an observable so all emissions (next, error, complete) re-enter NgZone.
 *
 * Use this for any observable that may emit outside Angular's zone (e.g., Auth0's
 * isAuthenticated$, user$, or any stream originating from MessageChannel/Web Workers).
 */
export function enterZone<T>(ngZone: NgZone) {
  return (source: Observable<T>): Observable<T> =>
    new Observable(observer => {
      const sub = source.subscribe({
        next: (value) => ngZone.run(() => observer.next(value)),
        error: (err) => ngZone.run(() => observer.error(err)),
        complete: () => ngZone.run(() => observer.complete()),
      })
      return () => sub.unsubscribe()
    })
}

/**
 * Ensures all HTTP responses trigger Angular change detection.
 *
 * Auth0's auth0-spa-js uses MessageChannel + Web Workers for token refresh.
 * MessageChannel.port.onmessage callbacks can resolve outside Angular's zone,
 * causing the entire HTTP interceptor chain to run outside the zone.
 * This interceptor re-enters NgZone for every response to guarantee
 * change detection fires.
 */
@Injectable()
export class ZoneAwareInterceptor implements HttpInterceptor {
  constructor(private ngZone: NgZone) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(enterZone(this.ngZone))
  }
}
